/**
 * useMoveVoiceover Hook
 * 
 * React hook for managing TTS (Text-to-Speech) for chess move annotations
 * Handles speech lifecycle, timing, and cleanup
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { speakWithFallback, stopWithFallback, TTSQueue } from '../util/ttsService';
import { 
  onWord, 
  createTTSCompletionPromise, 
  cleanTextForTTS,
  estimateReadMs 
} from '../util/ttsTiming';

/**
 * Hook for managing move voiceover with TTS
 * @param {boolean} enabled - Whether TTS is enabled
 * @param {string} text - Text to speak (raw annotation content)
 * @param {Object} options - Configuration options
 * @param {number} options.rate - Speech rate (0.5-2.0)
 * @param {number} options.voiceIndex - Selected voice index
 * @param {string} options.lang - Language code
 * @param {Function} options.onComplete - Callback when speech completes
 * @param {Function} options.onStart - Callback when speech starts
 * @returns {Object} - { isSpeaking, duration, stop, speak }
 */
export function useMoveVoiceover(enabled, text, options = {}) {
  const {
    rate = 1.0,
    voiceIndex,
    lang = 'en-US',
    onComplete,
    onStart
  } = options;
  
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [duration, setDuration] = useState(0);
  const listenerRef = useRef(null);
  const completionTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  
  // Clean text for TTS
  const cleanText = cleanTextForTTS(text);
  
  // Manual stop function
  const stop = useCallback(async () => {
    // Stop speech
    await stopWithFallback();
    
    // Clean up listener
    if (listenerRef.current && typeof listenerRef.current.remove === 'function') {
      try {
        await listenerRef.current.remove();
      } catch (error) {
        console.error('Error removing TTS listener:', error);
      }
      listenerRef.current = null;
    }
    
    // Clear timer
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    
    // Update state
    if (isMountedRef.current) {
      setIsSpeaking(false);
    }
  }, []);
  
  // Manual speak function
  const speak = useCallback(async (overrideText) => {
    const textToSpeak = overrideText ? cleanTextForTTS(overrideText) : cleanText;
    
    if (!textToSpeak) return;
    
    // Stop any existing speech
    await stop();
    
    // Update state
    if (isMountedRef.current) {
      setIsSpeaking(true);
      const estimatedMs = estimateReadMs(textToSpeak, rate);
      setDuration(estimatedMs);
    }
    
    // Call onStart callback
    if (onStart) {
      onStart();
    }
    
    // Set up completion detection
    let completed = false;
    
    const handleCompletion = async () => {
      if (completed) return;
      completed = true;
      
      // Clean up listener
      if (listenerRef.current && typeof listenerRef.current.remove === 'function') {
        try {
          await listenerRef.current.remove();
        } catch (error) {
          console.error('Error removing TTS listener in completion:', error);
        }
        listenerRef.current = null;
      }
      
      // Clear timer
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
      
      // Update state
      if (isMountedRef.current) {
        setIsSpeaking(false);
      }
      
      // Call completion callback
      if (onComplete) {
        onComplete();
      }
    };
    
    // Subscribe to word boundary events
    try {
      listenerRef.current = onWord(({ end }) => {
        // Check if we've reached the end
        if (end >= textToSpeak.length) {
          handleCompletion().catch(console.error);
        }
      });
    } catch (error) {
      console.error('Failed to set up word listener:', error);
    }
    
    // Set fallback completion timer
    const fallbackMs = estimateReadMs(textToSpeak, rate) + 750;
    completionTimerRef.current = setTimeout(() => {
      handleCompletion().catch(console.error);
    }, fallbackMs);
    
    // Speak the text
    try {
      await speakWithFallback(textToSpeak, {
        rate,
        voiceIndex,
        lang,
        queue: TTSQueue.Flush,
        category: 'playback' // For iOS screen recording
      });
    } catch (error) {
      console.error('TTS speak error:', error);
      handleCompletion().catch(console.error);
    }
  }, [cleanText, rate, voiceIndex, lang, stop, onComplete, onStart]);
  
  // Auto-speak when enabled and text changes
  useEffect(() => {
    if (enabled && cleanText) {
      speak();
    } else if (!enabled) {
      stop();
    }
    
    // Cleanup on unmount or when disabled
    return () => {
      if (!enabled) {
        stop();
      }
    };
  }, [enabled, cleanText, speak, stop]);
  
  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      stop();
    };
  }, [stop]);
  
  return {
    isSpeaking,
    duration,
    stop,
    speak
  };
}

/**
 * Hook specifically for Record mode autoplay with TTS
 * Manages the timing coordination between moves and speech
 */
export function useRecordModeTTS(enabled, options = {}) {
  const {
    rate = 1.0,
    voiceIndex,
    lang = 'en-US'
  } = options;
  
  const [currentlySpeaking, setCurrentlySpeaking] = useState(false);
  const speakingRef = useRef(false);
  
  /**
   * Speak move annotation and return completion promise
   * @param {string} content - Annotation content to speak
   * @returns {Promise} Resolves when speech completes
   */
  const speakMoveAnnotation = useCallback(async (content) => {
    const cleanContent = cleanTextForTTS(content);
    
    // If TTS is disabled or no content, resolve immediately with estimated time
    if (!enabled || !cleanContent) {
      const duration = estimateReadMs(cleanContent || '', rate);
      return new Promise(resolve => setTimeout(resolve, duration));
    }
    
    // Stop any current speech
    await stopWithFallback();
    
    // Update speaking state
    speakingRef.current = true;
    setCurrentlySpeaking(true);
    
    // Create completion promise
    const completionPromise = createTTSCompletionPromise(cleanContent, rate);
    
    // Speak the text
    try {
      await speakWithFallback(cleanContent, {
        rate,
        voiceIndex,
        lang,
        queue: TTSQueue.Flush,
        category: 'playback'
      });
    } catch (error) {
      console.error('TTS speak error:', error);
    }
    
    // Wait for completion
    await completionPromise;
    
    // Update speaking state
    speakingRef.current = false;
    setCurrentlySpeaking(false);
    
    return;
  }, [enabled, rate, voiceIndex, lang]);
  
  /**
   * Stop current speech
   */
  const stopSpeaking = useCallback(async () => {
    await stopWithFallback();
    speakingRef.current = false;
    setCurrentlySpeaking(false);
  }, []);
  
  /**
   * Calculate display time for a move (with or without TTS)
   * @param {string} content - Move annotation content
   * @returns {number} Display time in milliseconds
   */
  const calculateMoveTime = useCallback((content) => {
    const cleanContent = cleanTextForTTS(content);
    
    if (enabled && cleanContent) {
      // When TTS is enabled, use TTS timing
      return estimateReadMs(cleanContent, rate);
    } else {
      // Fallback to character-based timing when TTS is off
      const textLength = cleanContent.length;
      const baseTime = 750;
      
      if (textLength > 20) {
        return baseTime * 3;
      } else if (textLength > 10) {
        return baseTime * 2;
      } else {
        return baseTime;
      }
    }
  }, [enabled, rate]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSpeaking();
    };
  }, [stopSpeaking]);
  
  return {
    speakMoveAnnotation,
    stopSpeaking,
    calculateMoveTime,
    isSpeaking: currentlySpeaking
  };
}